import {Component, ElementRef, OnInit, Renderer2, ViewChild} from '@angular/core';
import {Localization} from '../../../../common/enums/localization';
import {InterventionTeamDetailDto} from "../intervention-team-detail.dto";
import {InterventionTeamResource} from "../../intervention-team.resource";
import {sortBy} from "../../../../../helpers/sorting";
import {EditNameDialogComponent} from "../edit-name-dialog/edit-name-dialog.component";
import {filter, map, takeUntil} from "rxjs/operators";
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {Activity} from '../../../../../helpers/activity';
import {InterventionTeamManagerState} from './intervention-team-manager.state';
import {Destroyable} from '../../../../../angular/component/destruction/destroyable.interface';
import {Subject} from 'rxjs';
import {SelfDestruct} from '../../../../../angular/component/destruction/self-destruct';


@Component({
  selector: 'app-intervention-team-manager',
  templateUrl: './intervention-team-manager.component.html',
  styleUrls: ['./intervention-team-manager.component.scss'],
})
export class InterventionTeamManagerComponent implements OnInit, Destroyable {
  public readonly destroyed$ = new Subject<InterventionTeamManagerComponent>;

  public teams: InterventionTeamDetailDto[];
  public readonly Localization = Localization;
  public searchPhrase: string;
  protected readonly activity = new Activity<InterventionTeamManagerActivity>(InterventionTeamManagerActivity);

  @ViewChild('teamList')
  protected teamList: ElementRef

  constructor(
    private readonly teamResource: InterventionTeamResource,
    private readonly dialogs: MatDialog,
    private readonly state: InterventionTeamManagerState,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(params => console.log(params.getAll('id')))
  }

  public get filteredTeams(): InterventionTeamDetailDto[] {
    if (this.searchPhrase?.trim()?.length > 0) {
      return this.teams?.filter(({label}) => label.includes(this.searchPhrase)) || [];
    }
    return this.teams;
  }

  public async ngOnInit(): Promise<void> {
    await this.fetchTeams();
    this.initialiseSelection();
    this.handleStateChanges();
  }

  public ngOnDestroy() {
    SelfDestruct(this);
  }

  public didUpdateTeam(team: InterventionTeamDetailDto): void {
    this.replaceTeam(team);
    this.sortTeams();
  }

  public addTeam(): void {
    this.dialogs.open(EditNameDialogComponent, {panelClass: 'full-bleed'})
      .afterClosed().pipe(filter(Boolean)).subscribe((team: InterventionTeamDetailDto) => {
      this.teams.push(team);
      this.sortTeams();
      this.openEditorFor(team);
    });
  }

  public didDeleteTeam(team: InterventionTeamDetailDto): void {
    const deletedTeamIndex = this.teams.findIndex(({id}) => id === team.id);
    this.teams.splice(deletedTeamIndex, 1);
    if (this.teams.length) {
      let selectionIndex = deletedTeamIndex > (this.teams.length - 1)
        ? this.teams.length - 1 : deletedTeamIndex;
      this.openEditorFor(this.teams[selectionIndex]);
    }
  }

  private replaceTeam(team: InterventionTeamDetailDto): void {
    const originalTeam: any = this.teams.find(({id}) => id === team.id);
    const originalPosition = this.teams.indexOf(originalTeam);
    if (originalPosition < 0) {
      throw new RangeError(`Could not locate team with ID ${team.id}`);
    }
    this.teams.splice(originalPosition, 1, team);
  }

  private async fetchTeams() {
    this.teams = (await this.allTeamsRequest).items;
    this.sortTeams();
  }

  private get allTeamsRequest() {
    return this.activity.track(InterventionTeamManagerActivity.LoadTeams)
      .operation(async () => await this.teamResource.all().toPromise());
  }

  private sortTeams(): void {
    this.teams.sort(sortBy<InterventionTeamDetailDto>('label'));
  }


  protected readonly InterventionTeamManagerActivity = InterventionTeamManagerActivity;

  private handleStateChanges() {
    this.state.onTeamDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(({team}) => this.didDeleteTeam(team));
    this.state.onTeamUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(({team}) => this.didUpdateTeam(team));
  }

  private openEditorFor(team: InterventionTeamDetailDto) {
    this.router.navigate(['maintenance', 'intervention-teams', 'teams', team.id]);
    this.scrollToActiveTeam();
  }

  private scrollToActiveTeam() {
    setTimeout(() => {
      const active = this.teamList.nativeElement.querySelector('.active') as HTMLElement;
      active.scrollIntoView({behavior: 'smooth'});
    });
  }

  private  initialiseSelection() {
    const path = window.location.pathname.split('/');
    if (path.pop() === 'teams') {
      this.openEditorFor(this.teams[0]);
    }
  }
}

enum InterventionTeamManagerActivity {
  LoadTeams,
  DeleteTeam,
  AddTeam,
  UpdateTEam,
}
